import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import FotoCaptura from '../components/FotoCaptura';

const CATEGORIAS = [
  'Transporte',
  'Peajes',
  'Estacionamiento',
  'Combustible',
  'Materiales',
  'Herramientas',
  'Alimentación',
  'Otros',
];

const ESTADO_CONFIG = {
  borrador:  { label: 'Borrador',  bg: '#1C2922', color: '#8A9994' },
  enviada:   { label: 'Enviada',   bg: '#0D1E2E', color: '#38BDF8' },
  aprobada:  { label: 'Aprobada',  bg: '#12382D', color: '#4CAF50' },
  rechazada: { label: 'Rechazada', bg: '#2D0F0F', color: '#EF4444' },
  pagada:    { label: 'Pagada',    bg: '#1E1228', color: '#A855F7' },
};

function Badge({ estado }) {
  const cfg = ESTADO_CONFIG[estado] || ESTADO_CONFIG.borrador;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 600,
    }}>{cfg.label}</span>
  );
}

function formatCLP(n) {
  if (!n && n !== 0) return '—';
  return `$${Number(n).toLocaleString('es-CL')}`;
}

function formatPeriodo(val) {
  if (!val) return '—';
  const s = String(val).slice(0, 7);
  const [y, m] = s.split('-');
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${meses[parseInt(m) - 1]} ${y}`;
}

// ── Modal genérico ─────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 200, padding: '0 0 0 0',
    }}>
      <div style={{
        background: '#111714', borderRadius: '16px 16px 0 0',
        padding: '24px 20px 32px', width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 20,
                      borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#F4F7F5' }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 24,
            cursor: 'pointer', color: '#8A9994', lineHeight: 1,
          }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Formulario de ítem ─────────────────────────────────────────────────────────
function FormItem({ inicial, onGuardar, onCancelar, guardando }) {
  const [form, setForm] = useState(
    inicial || {
      categoria: 'Transporte', descripcion: '', monto_clp: '', foto_boleta: '',
      rut_proveedor: '', tipo_documento: '', numero_documento: '',
    }
  );
  // Fotos del ítem (múltiples). En edición vienen en inicial.fotos ([{foto,...}]).
  const [fotos, setFotos] = useState(
    (inicial?.fotos || []).map(f => f.foto).filter(Boolean)
  );

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleEnviar = () => {
    if (!form.descripcion.trim()) { alert('Ingresa una descripción'); return; }
    const monto = parseInt(form.monto_clp);
    if (!monto || monto <= 0) { alert('Ingresa un monto válido'); return; }
    onGuardar({ ...form, monto_clp: monto, fotos });
  };

  const inputSt = {
    width: '100%', padding: '11px 13px',
    border: '1.5px solid #2E4A40', borderRadius: 9,
    fontSize: 14, fontFamily: 'inherit',
    background: '#0D1311', color: '#F4F7F5',
  };

  return (
    <div>
      <div className="form-group">
        <label style={{ color: '#8A9994' }}>Categoría</label>
        <select value={form.categoria} onChange={e => set('categoria', e.target.value)}
          style={inputSt}>
          {CATEGORIAS.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="form-group">
        <label style={{ color: '#8A9994' }}>Descripción</label>
        <input type="text" value={form.descripcion}
          onChange={e => set('descripcion', e.target.value)}
          placeholder="Ej: Taxi al edificio Los Jardines"
          style={inputSt} />
      </div>

      <div className="form-group">
        <label style={{ color: '#8A9994' }}>Monto (CLP)</label>
        <input type="number" value={form.monto_clp}
          onChange={e => set('monto_clp', e.target.value)}
          placeholder="Ej: 5500"
          min="1"
          style={inputSt} />
      </div>

      <div className="form-group">
        <label style={{ color: '#8A9994' }}>RUT Proveedor <span style={{ fontWeight: 400, color: '#6B7C74' }}>(opcional)</span></label>
        <input type="text" value={form.rut_proveedor}
          onChange={e => set('rut_proveedor', e.target.value)}
          placeholder="Ej: 76.123.456-7"
          style={inputSt} />
      </div>

      <div className="form-group">
        <label style={{ color: '#8A9994' }}>Tipo de documento <span style={{ fontWeight: 400, color: '#6B7C74' }}>(opcional)</span></label>
        <select value={form.tipo_documento} onChange={e => set('tipo_documento', e.target.value)}
          style={inputSt}>
          <option value="">Sin documento</option>
          <option value="boleta">Boleta</option>
          <option value="factura">Factura</option>
        </select>
      </div>

      {form.tipo_documento && (
        <div className="form-group">
          <label style={{ color: '#8A9994' }}>N° de {form.tipo_documento}</label>
          <input type="text" value={form.numero_documento}
            onChange={e => set('numero_documento', e.target.value)}
            placeholder={`Ej: ${form.tipo_documento === 'factura' ? '001-000456' : '005234'}`}
            style={inputSt} />
        </div>
      )}

      <div className="form-group">
        <label style={{ color: '#8A9994' }}>Fotos de boleta <span style={{ fontWeight: 400, color: '#6B7C74' }}>(opcional, hasta 5)</span></label>
        <FotoCaptura
          maxFotos={5}
          iniciales={fotos.map(d => ({ dataURL: d }))}
          onChange={arr => setFotos(arr.map(f => f.dataURL))}
        />
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button onClick={onCancelar} style={{
          flex: 1, padding: '12px', background: '#161C19',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, fontSize: 14,
          fontWeight: 600, cursor: 'pointer', color: '#C9D3CE',
        }}>Cancelar</button>
        <button onClick={handleEnviar} disabled={guardando} style={{
          flex: 2, padding: '12px',
          background: guardando ? '#0a4433' : '#0f6e56',
          border: 'none', borderRadius: 10, fontSize: 14,
          fontWeight: 700, cursor: 'pointer', color: 'white',
        }}>{guardando ? 'Guardando...' : 'Guardar gasto'}</button>
      </div>
    </div>
  );
}

// ── Página principal ───────────────────────────────────────────────────────────
export default function Rendiciones() {
  const navigate = useNavigate();

  const [vista,       setVista]       = useState('lista'); // 'lista' | 'detalle'
  const [rendiciones, setRendiciones] = useState([]);
  const [actual,      setActual]      = useState(null);   // rendición con items
  const [loading,     setLoading]     = useState(false);
  const [accion,      setAccion]      = useState(false);  // spinner operaciones

  // Modales
  const [modalNueva,  setModalNueva]  = useState(false);
  const [modalItem,   setModalItem]   = useState(null);   // null | { modo, item? }
  const [formNueva,   setFormNueva]   = useState({ periodo: '', descripcion: '' });
  const [fotoVista,   setFotoVista]   = useState(null);   // dataURL de boleta en visor

  // ── Cargar lista ─────────────────────────────────────────────────────────────
  const cargarLista = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/rendiciones');
      setRendiciones(r.data);
    } catch {
      alert('Error al cargar rendiciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarLista(); }, [cargarLista]);

  // ── Cargar detalle ────────────────────────────────────────────────────────────
  const cargarDetalle = async (id) => {
    setLoading(true);
    try {
      const r = await api.get(`/rendiciones/${id}`);
      setActual(r.data);
      setVista('detalle');
    } catch {
      alert('Error al cargar detalle');
    } finally {
      setLoading(false);
    }
  };

  // ── Crear rendición ───────────────────────────────────────────────────────────
  const crearRendicion = async () => {
    if (!formNueva.periodo) { alert('Selecciona el período'); return; }
    setAccion(true);
    try {
      const r = await api.post('/rendiciones', {
        periodo:     formNueva.periodo + '-01',
        descripcion: formNueva.descripcion || null,
      });
      setModalNueva(false);
      setFormNueva({ periodo: '', descripcion: '' });
      await cargarDetalle(r.data.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al crear rendición');
    } finally {
      setAccion(false);
    }
  };

  // ── Agregar / editar ítem ─────────────────────────────────────────────────────
  const guardarItem = async (datos) => {
    setAccion(true);
    try {
      if (modalItem.modo === 'crear') {
        await api.post(`/rendiciones/${actual.id}/items`, datos);
      } else {
        await api.put(`/rendiciones/${actual.id}/items/${modalItem.item.id}`, datos);
      }
      setModalItem(null);
      await cargarDetalle(actual.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al guardar gasto');
    } finally {
      setAccion(false);
    }
  };

  // ── Eliminar ítem ─────────────────────────────────────────────────────────────
  const eliminarItem = async (iid) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    setAccion(true);
    try {
      await api.delete(`/rendiciones/${actual.id}/items/${iid}`);
      await cargarDetalle(actual.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    } finally {
      setAccion(false);
    }
  };

  // ── Enviar para revisión ──────────────────────────────────────────────────────
  const enviarRendicion = async () => {
    if (!window.confirm('¿Enviar esta rendición para revisión? No podrás editarla después.')) return;
    setAccion(true);
    try {
      await api.post(`/rendiciones/${actual.id}/enviar`);
      await cargarDetalle(actual.id);
      await cargarLista();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al enviar');
    } finally {
      setAccion(false);
    }
  };

  // ── Reabrir rendición rechazada ───────────────────────────────────────────────
  const reabrirRendicion = async () => {
    if (!window.confirm('¿Reabrir esta rendición para corregirla?')) return;
    setAccion(true);
    try {
      await api.post(`/rendiciones/${actual.id}/reabrir`);
      await cargarDetalle(actual.id);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al reabrir');
    } finally {
      setAccion(false);
    }
  };

  // ── Eliminar rendición ────────────────────────────────────────────────────────
  const eliminarRendicion = async () => {
    if (!window.confirm('¿Eliminar esta rendición? Esta acción no se puede deshacer.')) return;
    setAccion(true);
    try {
      await api.delete(`/rendiciones/${actual.id}`);
      setVista('lista');
      setActual(null);
      await cargarLista();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    } finally {
      setAccion(false);
    }
  };

  const esBorrador = actual?.estado === 'borrador';

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER DETALLE
  // ═══════════════════════════════════════════════════════════════════════════
  if (vista === 'detalle' && actual) {
    return (
      <div className="app-container">
        <div className="app-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
            <button className="back-btn" onClick={() => { setVista('lista'); setActual(null); }}>←</button>
            <h1 style={{ flex: 1, margin: 0 }}>
              {actual.descripcion || `Rendición ${formatPeriodo(actual.periodo)}`}
            </h1>
          </div>
          {actual.descripcion && (
            <p style={{ margin: '2px 0 0 46px', fontSize: 12,
              color: 'rgba(255,255,255,0.7)' }}>
              {formatPeriodo(actual.periodo)}
            </p>
          )}
        </div>

        <div className="page-content">

          {/* Estado y total */}
          <div style={{
            background: '#161C19', borderRadius: 12,
            padding: '16px', marginBottom: 16,
            border: '1px solid rgba(255,255,255,0.07)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', marginBottom: 8 }}>
              <Badge estado={actual.estado} />
              <span style={{ fontSize: 20, fontWeight: 800, color: '#4CAF50' }}>
                {formatCLP(actual.total_clp)}
              </span>
            </div>
            {actual.descripcion && (
              <p style={{ fontSize: 13, color: '#8A9994', margin: 0 }}>{actual.descripcion}</p>
            )}
            <p style={{ fontSize: 12, color: '#6B7C74', margin: '6px 0 0' }}>
              {actual.n_items || actual.items?.length || 0} gasto{(actual.items?.length !== 1) ? 's' : ''}
            </p>
          </div>

          {/* Mensaje de rechazo */}
          {actual.estado === 'rechazada' && actual.comentario && (
            <div style={{
              background: '#2D0F0F', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 10,
              padding: '12px 14px', marginBottom: 16,
            }}>
              <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 13, color: '#EF4444' }}>
                Motivo de rechazo
              </p>
              <p style={{ margin: 0, fontSize: 13, color: '#8A9994' }}>{actual.comentario}</p>
            </div>
          )}

          {/* Mensaje de aprobación */}
          {actual.estado === 'aprobada' && (
            <div style={{
              background: '#12382D', border: '1px solid rgba(76,175,80,0.3)',
              borderRadius: 10,
              padding: '12px 14px', marginBottom: 16,
            }}>
              <p style={{ margin: 0, fontSize: 13, color: '#4CAF50', fontWeight: 600 }}>
                ✓ Rendición aprobada por {actual.revisado_por_nombre}
              </p>
            </div>
          )}

          {/* Mensaje de pago */}
          {actual.estado === 'pagada' && (
            <div style={{
              background: '#1E1228', border: '1px solid rgba(168,85,247,0.3)',
              borderRadius: 10,
              padding: '12px 14px', marginBottom: 16,
            }}>
              <p style={{ margin: 0, fontSize: 13, color: '#A855F7', fontWeight: 600 }}>
                💳 Rendición aprobada y pagada
              </p>
            </div>
          )}

          {/* Lista de ítems */}
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#6B7C74',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        marginBottom: 10 }}>Gastos</p>

            {(!actual.items || actual.items.length === 0) && (
              <div style={{
                textAlign: 'center', padding: '32px 20px',
                background: '#161C19', borderRadius: 12,
                border: '2px dashed rgba(255,255,255,0.07)',
              }}>
                <p style={{ fontSize: 28, marginBottom: 8 }}>🧾</p>
                <p style={{ color: '#6B7C74', fontSize: 14 }}>Sin gastos aún</p>
                {esBorrador && (
                  <p style={{ color: '#6B7C74', fontSize: 13, marginTop: 4 }}>
                    Agrega el primer gasto con el botón de abajo
                  </p>
                )}
              </div>
            )}

            {actual.items?.map((item, idx) => (
              <div key={item.id} style={{
                background: '#161C19', borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.07)', padding: '14px',
                marginBottom: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{
                        background: '#12382D', color: '#4CAF50',
                        padding: '2px 8px', borderRadius: 6,
                        fontSize: 11, fontWeight: 600,
                      }}>{item.categoria}</span>
                      {item.tipo_documento && (
                        <span style={{
                          background: '#0B0F0D', color: '#8A9994',
                          padding: '2px 8px', borderRadius: 6,
                          fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                        }}>
                          {item.tipo_documento}
                          {item.numero_documento ? ` N° ${item.numero_documento}` : ''}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: '#F4F7F5' }}>
                      {item.descripcion}
                    </p>
                    {item.rut_proveedor && (
                      <p style={{ margin: '0 0 4px', fontSize: 12, color: '#8A9994' }}>
                        RUT: {item.rut_proveedor}
                      </p>
                    )}
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#4CAF50' }}>
                      {formatCLP(item.monto_clp)}
                    </p>
                  </div>

                  {(item.fotos && item.fotos.length > 0) && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end',
                                  flexShrink: 0, maxWidth: 130 }}>
                      {item.fotos.map((f, idx) => (
                        <img key={f.id || idx} src={f.foto} alt="boleta"
                          style={{ width: 56, height: 56, objectFit: 'cover', cursor: 'pointer',
                                   borderRadius: 8, border: '1px solid rgba(255,255,255,0.07)' }}
                          onClick={() => setFotoVista(f.foto)} />
                      ))}
                    </div>
                  )}
                </div>

                {esBorrador && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, paddingTop: 10,
                                borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <button onClick={() => setModalItem({ modo: 'editar', item })} style={{
                      flex: 1, padding: '7px', background: '#0B0F0D',
                      border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, fontSize: 13,
                      fontWeight: 600, cursor: 'pointer', color: '#C9D3CE',
                    }}>✏ Editar</button>
                    <button onClick={() => eliminarItem(item.id)} style={{
                      flex: 1, padding: '7px', background: '#2D0F0F',
                      border: 'none', borderRadius: 8, fontSize: 13,
                      fontWeight: 600, cursor: 'pointer', color: '#EF4444',
                    }}>🗑 Eliminar</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Acciones */}
          {esBorrador && (
            <>
              <button className="btn btn-outline" style={{ marginBottom: 10 }}
                onClick={() => setModalItem({ modo: 'crear' })}>
                + Agregar gasto
              </button>
              {actual.items?.length > 0 && (
                <button className="btn btn-primary" style={{ marginBottom: 10 }}
                  disabled={accion} onClick={enviarRendicion}>
                  {accion ? 'Enviando...' : '📤 Enviar para revisión'}
                </button>
              )}
              <button className="btn" style={{ background: '#2D0F0F', color: '#EF4444', border: 'none' }}
                disabled={accion} onClick={eliminarRendicion}>
                Eliminar rendición
              </button>
            </>
          )}

          {actual.estado === 'rechazada' && (
            <button className="btn btn-primary" disabled={accion} onClick={reabrirRendicion}>
              {accion ? 'Reabriendo...' : '↩ Reabrir y corregir'}
            </button>
          )}
        </div>

        {/* Modal agregar / editar ítem */}
        {modalItem && (
          <Modal
            title={modalItem.modo === 'crear' ? 'Agregar gasto' : 'Editar gasto'}
            onClose={() => setModalItem(null)}
          >
            <FormItem
              inicial={modalItem.item
                ? {
                    ...modalItem.item,
                    monto_clp:        String(modalItem.item.monto_clp),
                    rut_proveedor:    modalItem.item.rut_proveedor    || '',
                    tipo_documento:   modalItem.item.tipo_documento   || '',
                    numero_documento: modalItem.item.numero_documento || '',
                  }
                : null}
              onGuardar={guardarItem}
              onCancelar={() => setModalItem(null)}
              guardando={accion}
            />
          </Modal>
        )}

        {/* Visor de boleta (imagen inline, no navega a data: URL) */}
        {fotoVista && (
          <div onClick={() => setFotoVista(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
                     display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <img src={fotoVista} alt="boleta"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
            <button onClick={() => setFotoVista(null)}
              style={{ position: 'absolute', top: 16, right: 20, background: 'rgba(0,0,0,0.6)',
                       color: '#fff', border: 'none', borderRadius: 20, width: 40, height: 40,
                       fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>
              ×
            </button>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER LISTA
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="app-container">
      <div className="app-header">
        <button className="back-btn" onClick={() => navigate('/')}>←</button>
        <h1>Rendición de gastos</h1>
      </div>

      <div className="page-content">

        {loading && <div className="spinner" />}

        {!loading && rendiciones.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '48px 20px',
            background: '#161C19', borderRadius: 16,
            border: '2px dashed rgba(255,255,255,0.07)', marginBottom: 20,
          }}>
            <p style={{ fontSize: 36, marginBottom: 12 }}>💰</p>
            <p style={{ fontWeight: 700, fontSize: 15, color: '#F4F7F5', marginBottom: 6 }}>
              Sin rendiciones
            </p>
            <p style={{ color: '#6B7C74', fontSize: 13 }}>
              Crea tu primera rendición de gastos del mes
            </p>
          </div>
        )}

        {rendiciones.map(r => (
          <div key={r.id}
            onClick={() => cargarDetalle(r.id)}
            style={{
              background: '#161C19', borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.07)', padding: '16px',
              marginBottom: 12, cursor: 'pointer',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'flex-start', marginBottom: 6 }}>
              <div>
                <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 15, color: '#F4F7F5' }}>
                  {r.descripcion || formatPeriodo(r.periodo)}
                </p>
                {r.descripcion && (
                  <p style={{ margin: 0, fontSize: 12, color: '#6B7C74' }}>
                    {formatPeriodo(r.periodo)}
                  </p>
                )}
              </div>
              <Badge estado={r.estado} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 12, color: '#6B7C74' }}>
                {r.n_items} gasto{r.n_items !== 1 ? 's' : ''}
              </span>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#4CAF50' }}>
                {formatCLP(r.total_clp)}
              </span>
            </div>
          </div>
        ))}

        <button className="btn btn-primary" onClick={() => setModalNueva(true)}
          style={{ marginTop: rendiciones.length > 0 ? 4 : 0 }}>
          + Nueva rendición
        </button>
      </div>

      {/* Modal nueva rendición */}
      {modalNueva && (
        <Modal title="Nueva rendición" onClose={() => setModalNueva(false)}>
          <div className="form-group">
            <label style={{ color: '#8A9994' }}>Período</label>
            <input type="month"
              value={formNueva.periodo}
              onChange={e => setFormNueva(p => ({ ...p, periodo: e.target.value }))}
              style={{
                width: '100%', padding: '11px 13px',
                border: '1.5px solid #2E4A40', borderRadius: 9,
                fontSize: 14, fontFamily: 'inherit',
                background: '#0D1311', color: '#F4F7F5',
              }} />
          </div>
          <div className="form-group">
            <label style={{ color: '#8A9994' }}>Descripción <span style={{ fontWeight: 400, color: '#6B7C74' }}>(opcional)</span></label>
            <input type="text"
              value={formNueva.descripcion}
              onChange={e => setFormNueva(p => ({ ...p, descripcion: e.target.value }))}
              placeholder="Ej: Gastos semana del 10 al 14"
              style={{
                width: '100%', padding: '11px 13px',
                border: '1.5px solid #2E4A40', borderRadius: 9,
                fontSize: 14, fontFamily: 'inherit',
                background: '#0D1311', color: '#F4F7F5',
              }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setModalNueva(false)} style={{
              flex: 1, padding: '12px', background: '#161C19',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, fontSize: 14,
              fontWeight: 600, cursor: 'pointer', color: '#C9D3CE',
            }}>Cancelar</button>
            <button onClick={crearRendicion} disabled={accion} style={{
              flex: 2, padding: '12px',
              background: accion ? '#0a4433' : '#0f6e56',
              border: 'none', borderRadius: 10, fontSize: 14,
              fontWeight: 700, cursor: 'pointer', color: 'white',
            }}>{accion ? 'Creando...' : 'Crear rendición'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
